import type {
  AutomationPromptPreset,
  AutomationRuntimeStatus,
  DebounceInterval,
  ExportAutomationConfig,
  ImportAutomationConfig,
} from '../services/api';

export interface AutomationRuntimeFields {
  runtime_status?: AutomationRuntimeStatus | null;
  runtime_error?: string | null;
}

export interface ImportAutomationDraft extends AutomationRuntimeFields {
  next_execution_at?: string | null;
  next_execution_error?: string | null;
  enabled: boolean;
  paused: boolean;
  connector_id: string;
  source_prefix: string;
  file_pattern: string;
  recursive: boolean;
  debounce_interval: DebounceInterval | string | null;
  prompt_id: string;

}

export interface ExportAutomationDraft extends AutomationRuntimeFields {
  enabled: boolean;
  paused: boolean;
  destination_connector_id: string;
  destination_subpath: string;
}

/**
 * Editable drafts that survive route changes and page reloads. A null side is
 * clean; server-owned runtime fields are stripped before this state is stored.
 */
export interface AutomationIndexDirtyDrafts {
  importDraft: ImportAutomationDraft | null;
  exportDraft: ExportAutomationDraft | null;
}

export type AutomationDirtyDraftsByIndex = Record<string, AutomationIndexDirtyDrafts>;

export type AutomationMessage = {
  type: 'success' | 'error' | 'info';
  text: string;
};

export interface ExportDestinationSubpathResult {
  value: string;
  error: string | null;
}

export const normalizeExportDestinationSubpath = (
  value: string,
): ExportDestinationSubpathResult => {
  const cleaned = String(value || '').trim().replace(/\\/g, '/');
  if (!cleaned) return { value: '', error: null };
  if (cleaned.startsWith('/')) {
    return { value: '', error: 'Use a folder path relative to the connector.' };
  }

  const parts: string[] = [];
  for (const rawPart of cleaned.split('/')) {
    const part = rawPart.trim();
    if (!part) continue;
    if (part === '.' || part === '..') {
      return { value: '', error: 'Folder paths cannot contain . or .. segments.' };
    }
    parts.push(part);
  }
  return { value: parts.join('/'), error: null };
};

export const createDefaultImportAutomationDraft = (): ImportAutomationDraft => ({
  enabled: false,
  paused: false,
  connector_id: '',
  source_prefix: '',
  file_pattern: '*',
  recursive: true,
  debounce_interval: '5m',
  prompt_id: '',
  runtime_status: null,
  runtime_error: null,
});

export const createDefaultExportAutomationDraft = (): ExportAutomationDraft => ({
  enabled: false,
  paused: false,
  destination_connector_id: '',
  destination_subpath: '',
  runtime_status: null,
  runtime_error: null,
});

export const AUTOMATION_DEBOUNCE_OPTIONS: readonly DebounceInterval[] = ['5m', '10m', '1h', '1d'];
export const isAutomationDebounceInterval = (
  value: string | null | undefined,
): value is DebounceInterval => (
  AUTOMATION_DEBOUNCE_OPTIONS.includes(
    String(value || '').trim().toLowerCase() as DebounceInterval,
  )
);

const toDebounceInterval = (
  value: unknown,
  isPresent: boolean,
): DebounceInterval | string | null => {
  if (!isPresent) return '5m';
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  if (isAutomationDebounceInterval(normalized)) {
    return normalized as DebounceInterval;
  }
  // Preserve an unsupported legacy server value so the UI can identify it as
  // unavailable. Silently coercing it to 5m would change the schedule on the
  // next otherwise-unrelated save.
  return normalized;
};

const hasOwn = (value: object, key: string): boolean => (
  Object.prototype.hasOwnProperty.call(value, key)
);

export const toImportAutomationDraft = (
  config: ImportAutomationConfig | null,
): ImportAutomationDraft => {
  if (!config) {
    return createDefaultImportAutomationDraft();
  }
  const promptId = config.prompt_preset.prompt_id.trim();
  return {
    enabled: !!config.enabled,
    next_execution_at: config.next_execution_at,
    next_execution_error: config.next_execution_error,
    // runtime_status is the canonical observed state. Legacy/interrupted
    // responses can report `paused: false` while the scheduler is paused; keep
    // that state in the editable draft so an unrelated save cannot resume it.
    paused: !!config.enabled && (!!config.paused || config.runtime_status === 'paused'),
    connector_id: config.connector_id || '',
    source_prefix: config.source_prefix || '',
    file_pattern: config.file_pattern || '*',
    recursive: config.recursive !== false,
    debounce_interval: toDebounceInterval(
      config.debounce_interval,
      hasOwn(config, 'debounce_interval'),
    ),
    prompt_id: promptId,
    runtime_status: config.runtime_status ?? null,
    runtime_error: config.runtime_error ?? null,
  };
};

export const getImportAutomationDraftContractError = (
  draft: ImportAutomationDraft,
): string | null => {
  return draft.prompt_id.trim() ? null : 'Select an extraction engine.';
};

export const buildImportAutomationPromptPreset = (
  draft: ImportAutomationDraft,
): AutomationPromptPreset => {
  const contractError = getImportAutomationDraftContractError(draft);
  if (contractError) {
    throw new Error(`Invalid import automation prompt preset: ${contractError}`);
  }
  return {
    prompt_id: draft.prompt_id,
  };
};

export const toExportAutomationDraft = (
  config: ExportAutomationConfig | null,
): ExportAutomationDraft => {
  if (!config) {
    return createDefaultExportAutomationDraft();
  }
  return {
    enabled: !!config.enabled,
    paused: !!config.enabled && (!!config.paused || config.runtime_status === 'paused'),
    destination_connector_id: config.destination_connector_id || '',
    destination_subpath: config.destination_subpath || '',
    runtime_status: config.runtime_status ?? null,
    runtime_error: config.runtime_error ?? null,
  };
};

export const isAutomationPaused = (
  automation: AutomationRuntimeFields & { paused: boolean },
): boolean => automation.paused || automation.runtime_status === 'paused';

export const getAutomationRuntimeBadge = (
  automation: AutomationRuntimeFields & { enabled: boolean; paused: boolean },
): { className: string; label: string } => {
  if (automation.runtime_status === 'scheduler_error') {
    return { className: 'error', label: 'Error' };
  }
  if (automation.runtime_status === 'provisioning') {
    return { className: 'provisioning', label: 'Provisioning' };
  }
  if (!automation.enabled) {
    return { className: 'disabled', label: 'Off' };
  }
  if (isAutomationPaused(automation)) {
    return { className: 'paused', label: 'Paused' };
  }
  return { className: 'active', label: 'Active' };
};

export const getAutomationRuntimeError = (
  automation: AutomationRuntimeFields,
  fallbackMessage: string,
): string | null => {
  if (automation.runtime_status !== 'scheduler_error') {
    return null;
  }
  const runtimeError = typeof automation.runtime_error === 'string'
    ? automation.runtime_error.trim()
    : '';
  return runtimeError
    ? `Automation setup failed: ${runtimeError}`
    : fallbackMessage;
};

export const getAutomationMutationMessage = (
  automation: AutomationRuntimeFields,
  successMessage: string,
  failureMessage: string,
): AutomationMessage => {
  const runtimeError = getAutomationRuntimeError(automation, failureMessage);
  if (runtimeError) {
    return { type: 'error', text: runtimeError };
  }
  if (automation.runtime_status === 'provisioning') {
    return {
      type: 'info',
      text: 'Settings were saved and scheduler provisioning is still in progress.',
    };
  }
  return { type: 'success', text: successMessage };
};
